import { useEffect, useMemo, useRef, useState } from 'react';
import quoteServices from '../../../../../../services/oneButtonServices/quoteServices';
import { useOneButtonStore } from '../../../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../../../store/store';
import {
    getSolarMetricsSyncDetail,
    parseDesignDataRecord,
    resolveSolarAnnualProductionKwh,
    systemSizeKwFromSnapshot,
} from '../../../../../../helpers/systemDesignSolarMetrics';
import { effectiveAnnualUsageKwhForPricing } from '../../../../../../helpers/utilityMonthlyDailyToAnnual';
import { pricingProductTypeNames, activeOpportunityProductTypeNames } from '../../../../../../helpers/financeSelectors';
import {
    batteryDesignSourceLabel,
    fetchOneDrawRoofSquares,
    opportunityNeedsBatteryDesign,
    opportunityNeedsRoofSquares,
    resolveBatteryDesignForPricing,
    resolveRoofSquares,
    roofSquaresSourceLabel,
    sumOneDrawDesignListSquares,
    sumOneDrawRoofSquareTable,
} from '../../../../../../helpers/productDesignFallbacks';
import type { RoofSquareSplit } from '../../../../../../helpers/productDesignFallbacks.types';
import {
    applyBatteryDesignFallbacks,
    formatBatteryCount,
    formatMetricEpc,
    formatMetricTotalPrice,
    formatRoofEpcFromQuote,
    formatSolarEpcFromQuote,
    formatSolarRateKwh,
    getLinkedProductQuoteValues,
    resolveBatteryDesignDisplay,
    resolveSolarRateKwh,
} from './linkedProductQuoteMetrics';
import type { DealOpportunityProductAdder } from '../../../../../../services/oneButtonServices/quoteServices';
import { formatAdderPriceDisplay, sumAdderPriceForProductType } from './dealAdderProductTotals';

export type V3ProductKind = 'solar' | 'roof' | 'battery' | 'gutter';

export type V3Metric = {
    label: string;
    value: string;
    emphasis?: boolean;
    /** Live oneDRAW value shown beside the finalized one when the two disagree. */
    liveHint?: string | null;
};

export type V3ProductCardModel = {
    kind: V3ProductKind;
    title: string;
    subtitle: string;
    statusLabel: string;
    statusTone: 'signed' | 'selected' | 'neutral';
    metrics: V3Metric[];
    financePartner: string;
    financeMethod: string;
    showWastage?: boolean;
    wastagePercent?: number;
    shingleLabel?: string;
    shingleSwatch?: string;
    /** Shown when roof/battery values are estimated because opportunity design is missing. */
    fallbackHint?: string | null;
};

export type V3FinanceLineModel = {
    id: string;
    partner: string;
    method: string;
    monthly: number;
    productTags: string[];
};

export type V3PartnerSyncRow = {
    id: string;
    name: string;
    status: 'connected' | 'pending' | 'off';
    statusDetail: string;
    quotesCount: string;
    signedCount: string;
    enabled: boolean;
};

function normPartner(v: string): string {
    return String(v || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
}

function oppProductRow(opp: any, productName: string): any | null {
    const rows = opp?.OpportunityProduct ?? opp?.opportunityProduct ?? [];
    if (!Array.isArray(rows)) return null;
    const match = (p: any, name: string) =>
        String(p?.Product_Type_Name ?? '').toLowerCase() === name.toLowerCase();
    const found = rows.find((p: any) => match(p, productName));
    if (found) return found;
    if (productName.toLowerCase() === 'roof') {
        return rows.find((p: any) => match(p, 'rrr')) ?? null;
    }
    return null;
}

function financePartnerInfo(
    opp: any,
    productName: string
): Pick<V3ProductCardModel, 'financePartner' | 'financeMethod'> {
    const row = oppProductRow(opp, productName);
    if (!row) return { financePartner: 'Select Finance', financeMethod: '' };
    const partner = String(
        row.Finance_Partner_Display_Name ??
            row.FinancePartnerDisplayName ??
            row.Finance_Partner ??
            row.finance_Partner ??
            ''
    ).trim();
    const method = String(
        row.Finance_Method_Name ?? row.FinanceMethod ?? row.finance_Method_Name ?? ''
    ).trim();
    const placeholder = /^(select\s+(finance\s+)?partner|select\s+finance|select\s+method)$/i;
    return {
        financePartner: partner && !placeholder.test(partner) ? partner : 'Select Finance',
        financeMethod: method && !placeholder.test(method) ? method : '',
    };
}

function productStatus(row: any | null): { label: string; tone: 'signed' | 'selected' | 'neutral' } {
    if (!row) return { label: 'Selected', tone: 'selected' };
    const signed = row.IsSigned === true || row.isSigned === true || String(row.Status ?? '').toLowerCase() === 'signed';
    if (signed) return { label: 'Signed', tone: 'signed' };
    if (row.ActiveFlag === true || row.activeFlag === true) return { label: 'Selected', tone: 'selected' };
    return { label: 'Selected', tone: 'neutral' };
}

export type UseDashboardV3DataOptions = {
    /** Prefer viewer module count when design array count is unavailable */
    moduleCountOverride?: number | null;
    /** Bump after Add Adders save so card totals reload (optional; hook also loads on mount). */
    dealAddersRefreshKey?: number;
    /** System & Design: use RetrieveSystemDesign snapshot only — not live 3D preview. */
    finalizedDesignMetricsOnly?: boolean;
};

export function useDashboardV3Data(options?: UseDashboardV3DataOptions) {
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const systemDesign = useOneButtonStore((s) => s.systemDesign);
    const solarUtilityData = useOneButtonStore((s) => s.solarUtilityData);
    const priceQuoteOverrides = useOneButtonStore((s) => s.priceQuoteOverrides);
    const priceQuoteDetails = useOneButtonStore((s) => s.priceQuoteDetails);
    const financingGrid = useOneButtonStore((s) => s.financingGrid);
    const selectedFinanceCells = useOneButtonStore((s) => s.selectedFinanceCells);
    const financePartnersList = useOneButtonStore((s) => s.financePartnersList);
    const financePartnerSyncStatus = useOneButtonStore((s) => s.financePartnerSyncStatus);
    const systemDesignRefreshing = useOneButtonStore((s) => s.systemDesignRefreshing);
    const batteryModelData = useOneButtonStore((s) => s.batteryModelData);
    const productTypeAdders = useOneButtonStore((s) => s.productTypeAdders);
    const setProductTypeAdders = useOneButtonStore((s) => s.setProductTypeAdders);
    const [dealProductAdders, setDealProductAdders] = useState<DealOpportunityProductAdder[]>([]);

    const opportunityNumericId = useMemo(() => {
        const raw =
            opportunityDetails?.Opportunity_Id ??
            opportunityDetails?.OpportunityId ??
            null;
        return raw != null ? String(raw).trim() : '';
    }, [opportunityDetails]);

    useEffect(() => {
        if (!opportunityNumericId) {
            setDealProductAdders([]);
            return;
        }
        let cancelled = false;
        void (async () => {
            try {
                const rows = await quoteServices.DealOpportunityProductAdders(opportunityNumericId);
                if (!cancelled) setDealProductAdders(Array.isArray(rows) ? rows : []);
            } catch {
                if (!cancelled) setDealProductAdders([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [opportunityNumericId, options?.dealAddersRefreshKey]);

    useEffect(() => {
        if (productTypeAdders.length > 0) return;
        let cancelled = false;
        void quoteServices
            .getProductTypesAdders()
            .then((adders) => {
                if (!cancelled) setProductTypeAdders(Array.isArray(adders) ? adders : []);
            })
            .catch(() => {
                if (!cancelled) setProductTypeAdders([]);
            });
        return () => {
            cancelled = true;
        };
    }, [productTypeAdders.length, setProductTypeAdders]);
    const viewerActualAnnualKwh = useViewerStore((s) => s.actualWattage);
    const SFannualUsage = useViewerStore((s) => s.SFannualUsage);
    const viewerTotalSystemKw = useViewerStore((s) => s.totalSystemSize);
    const viewerModeCount = useViewerStore((s) => s.modeCount);
    const viewerMultiplier = useViewerStore((s) => s.multiplier);
    const isAiGeneratedDesign = useViewerStore((s) => s.isAiGeneratedDesign);
    const siteViewerCalculationsPending = useViewerStore((s) => s.siteViewerCalculationsPending);
    const roofSquareTableData = useViewerStore((s) => s.roofSquareTableData);
    const designList = useViewerStore((s) => s.designList);
    const [oneDrawFetchedSquares, setOneDrawFetchedSquares] = useState<RoofSquareSplit | null>(null);
    /** Prevents re-fetch / setState loops when oneDRAW returns null and source stays design-array. */
    const oneDrawRoofFetchKeyRef = useRef<string | null>(null);

    const sd = (systemDesign || {}) as Record<string, unknown>;
    const designData = useMemo(() => parseDesignDataRecord(sd), [sd]);

    const systemKw = useMemo(() => {
        const fromDesign = systemSizeKwFromSnapshot(sd, designData);
        if (fromDesign != null) return fromDesign;
        if (options?.finalizedDesignMetricsOnly) return null;
        return typeof viewerTotalSystemKw === 'number' && viewerTotalSystemKw > 0 ? viewerTotalSystemKw : null;
    }, [sd, designData, viewerTotalSystemKw, options?.finalizedDesignMetricsOnly]);

    const multiplierRaw = sd.Multiplier_Unrounded ?? sd.Multiplier;
    const snapshotMultiplierNum =
        multiplierRaw != null && Number.isFinite(Number(multiplierRaw)) ? Number(multiplierRaw) : null;
    const liveMultiplierNum =
        typeof viewerMultiplier === 'number' && Number.isFinite(viewerMultiplier) && viewerMultiplier > 0
            ? viewerMultiplier
            : null;
    // AI-applied layouts keep the previous RetrieveSystemDesign snapshot until finalize.
    // Show the live roof-weighted multiplier (what oneDRAW saves) instead of that stale figure.
    const multiplierNum =
        isAiGeneratedDesign && liveMultiplierNum != null ? liveMultiplierNum : snapshotMultiplierNum;

    const productionKwh = useMemo(
        () =>
            resolveSolarAnnualProductionKwh({
                sd,
                designData,
                viewerActualAnnualKwh,
                viewerModeCount,
                siteViewerCalculationsPending,
                systemKw,
                multiplier: multiplierNum,
                snapshotOnly: options?.finalizedDesignMetricsOnly,
            }),
        [
            sd,
            designData,
            viewerActualAnnualKwh,
            viewerModeCount,
            siteViewerCalculationsPending,
            systemKw,
            multiplierNum,
            options?.finalizedDesignMetricsOnly,
        ],
    );

    const utilitySolar = (solarUtilityData?.SolarUtility ?? {}) as Record<string, unknown>;
    const annualUsage = effectiveAnnualUsageKwhForPricing(utilitySolar, SFannualUsage);
    const offsetPct =
        productionKwh != null && annualUsage > 0
            ? Math.min(100, Math.round((productionKwh / annualUsage) * 100))
            : null;

    const multiplierDisplay = multiplierNum ?? '—';

    const moduleCount = useMemo(() => {
        if (!options?.finalizedDesignMetricsOnly) {
            const override = options?.moduleCountOverride;
            if (typeof override === 'number' && override > 0) return override;
        }
        const arr = sd.DesignArray;
        if (!Array.isArray(arr)) return null;
        let n = 0;
        for (const item of arr) {
            if (!item || typeof item !== 'object') continue;
            const row = item as Record<string, unknown>;
            n += Number(row.Quantity ?? row.ModuleCount ?? row.Module_Quantity) || 0;
        }
        return n > 0 ? n : null;
    }, [sd, options?.moduleCountOverride, options?.finalizedDesignMetricsOnly]);

    const finalizedSystemKw = useMemo(
        () => systemSizeKwFromSnapshot(sd, designData),
        [sd, designData],
    );

    const solarMetricsSync = useMemo(
        () => {
            if (systemDesignRefreshing) {
                return {
                    outOfSync: false,
                    systemKwOutOfSync: false,
                    multiplierOutOfSync: false,
                    liveSystemKw: null,
                    liveMultiplier: null,
                };
            }
            return getSolarMetricsSyncDetail({
                finalizedSystemKw,
                finalizedMultiplier: snapshotMultiplierNum,
                liveSystemKw:
                    typeof viewerTotalSystemKw === 'number' && viewerTotalSystemKw > 0
                        ? viewerTotalSystemKw
                        : null,
                liveMultiplier: liveMultiplierNum,
                calculationsPending: siteViewerCalculationsPending,
                liveModuleCount: viewerModeCount,
            });
        },
        [
            systemDesignRefreshing,
            finalizedSystemKw,
            snapshotMultiplierNum,
            liveMultiplierNum,
            viewerTotalSystemKw,
            siteViewerCalculationsPending,
            viewerModeCount,
        ],
    );

    const solarMetricsOutOfSync = solarMetricsSync.outOfSync;

    const solarLiveHints = useMemo(() => {
        const { liveSystemKw, liveMultiplier } = solarMetricsSync;
        return {
            systemKw:
                solarMetricsSync.systemKwOutOfSync && liveSystemKw != null
                    ? `oneDRAW: ${liveSystemKw.toFixed(2)} kW`
                    : null,
            multiplier:
                solarMetricsSync.multiplierOutOfSync &&
                liveMultiplier != null &&
                !(isAiGeneratedDesign && liveMultiplierNum != null)
                    ? `oneDRAW: ${Number(liveMultiplier.toFixed(3))}`
                    : null,
        };
    }, [solarMetricsSync, isAiGeneratedDesign, liveMultiplierNum]);

    const linkedSolarQuote = useMemo(
        () => getLinkedProductQuoteValues('solar', opportunityDetails, priceQuoteDetails),
        [opportunityDetails, priceQuoteDetails],
    );
    const linkedRoofQuote = useMemo(
        () => getLinkedProductQuoteValues('roof', opportunityDetails, priceQuoteDetails),
        [opportunityDetails, priceQuoteDetails],
    );
    const linkedBatteryQuote = useMemo(
        () => getLinkedProductQuoteValues('battery', opportunityDetails, priceQuoteDetails),
        [opportunityDetails, priceQuoteDetails],
    );

    const pricingProductTypes = useMemo(
        () => pricingProductTypeNames(opportunityDetails),
        [opportunityDetails],
    );
    const needsRoofSquares = opportunityNeedsRoofSquares(pricingProductTypes);
    const needsBatteryDesign = opportunityNeedsBatteryDesign(pricingProductTypes);

    const oneDrawTableSquares = useMemo(
        () => sumOneDrawRoofSquareTable(roofSquareTableData),
        [roofSquareTableData],
    );
    const oneDrawDesignListSquares = useMemo(
        () => sumOneDrawDesignListSquares(designList),
        [designList],
    );

    const roofSquaresResolved = useMemo(
        () =>
            resolveRoofSquares({
                systemDesign,
                priceQuoteOverrides,
                priceQuoteDetails,
                oneDrawTable: oneDrawTableSquares,
                oneDrawDesignList: oneDrawDesignListSquares,
                oneDrawFetched: oneDrawFetchedSquares,
                designArrayModules: moduleCount,
                includeRoofProduct: needsRoofSquares,
            }),
        [
            systemDesign,
            priceQuoteOverrides,
            priceQuoteDetails,
            oneDrawTableSquares,
            oneDrawDesignListSquares,
            oneDrawFetchedSquares,
            moduleCount,
            needsRoofSquares,
        ],
    );

    useEffect(() => {
        if (!needsRoofSquares) {
            oneDrawRoofFetchKeyRef.current = null;
            setOneDrawFetchedSquares((prev) => (prev == null ? prev : null));
            return;
        }
        // Prefer override / opportunity / onedraw. Keep trying while on design-array (module-count last resort).
        if (
            roofSquaresResolved.source === 'override' ||
            roofSquaresResolved.source === 'opportunity' ||
            roofSquaresResolved.source === 'onedraw'
        ) {
            return;
        }
        const sfid = String(
            opportunityDetails?.SalesForce_Id ?? systemDesign?.SalesForce_Id ?? '',
        ).trim();
        if (!sfid) return;

        const applyRoofWastage = systemDesign?.ApplyRoofWastage === true;
        const designSource = String(systemDesign?.Design_Source ?? opportunityDetails?.Design_Source ?? '');
        const fetchKey = `${sfid}::${designSource}::${applyRoofWastage ? '1' : '0'}`;
        if (oneDrawRoofFetchKeyRef.current === fetchKey) return;

        let cancelled = false;
        void fetchOneDrawRoofSquares({
            salesforceId: sfid,
            designSource,
            applyRoofWastage,
        }).then((result) => {
            if (cancelled) return;
            oneDrawRoofFetchKeyRef.current = fetchKey;
            setOneDrawFetchedSquares((prev) => {
                if (prev == null && result == null) return prev;
                if (
                    prev != null &&
                    result != null &&
                    prev.low === result.low &&
                    prev.standard === result.standard &&
                    prev.total === result.total
                ) {
                    return prev;
                }
                return result;
            });
        });
        return () => {
            cancelled = true;
        };
    }, [
        needsRoofSquares,
        roofSquaresResolved.source,
        opportunityDetails?.SalesForce_Id,
        opportunityDetails?.Design_Source,
        systemDesign?.SalesForce_Id,
        systemDesign?.Design_Source,
        systemDesign?.ApplyRoofWastage,
    ]);

    const batteryFallback = useMemo(
        () => resolveBatteryDesignForPricing(systemDesign, { includeBatteryProduct: needsBatteryDesign }),
        [systemDesign, needsBatteryDesign],
    );

    const epcSolar = formatSolarEpcFromQuote(linkedSolarQuote);

    const solarTotalPrice =
        linkedSolarQuote?.totalPrice != null
            ? formatMetricTotalPrice(linkedSolarQuote.totalPrice, linkedSolarQuote.isLinkedAgreement)
            : null;

    const solarRateKwh = useMemo(
        () =>
            resolveSolarRateKwh({
                linkedSolarQuote,
                priceQuoteOverrides,
                priceQuoteDetails,
                utilitySolar,
            }),
        [linkedSolarQuote, priceQuoteOverrides, priceQuoteDetails, utilitySolar],
    );

    const solarRateDisplay = formatSolarRateKwh(solarRateKwh);

    const roofSq = roofSquaresResolved.total > 0 ? roofSquaresResolved.total : null;
    const roofFallbackHint = needsRoofSquares ? roofSquaresSourceLabel(roofSquaresResolved.source) : null;
    const roof = sd.Roof && !Array.isArray(sd.Roof) ? (sd.Roof as Record<string, unknown>) : null;
    const roofPpsfRaw =
        priceQuoteOverrides?.pricePerSqFt != null
            ? Number(priceQuoteOverrides.pricePerSqFt)
            : roof?.PricePerSqft != null || roof?.PricePerSqFt != null
              ? Number(roof?.PricePerSqft ?? roof?.PricePerSqFt)
              : null;
    const roofPpsfDisplay =
        roofPpsfRaw != null && Number.isFinite(roofPpsfRaw) && roofPpsfRaw > 0
            ? `$${roofPpsfRaw.toFixed(2)}`
            : '—';
    const roofEpcDisplay = formatRoofEpcFromQuote(linkedRoofQuote);
    const shingle =
        priceQuoteOverrides?.selectedShingleAlt ??
        (roof?.ShingleColor != null && String(roof.ShingleColor).trim() !== ''
            ? String(roof.ShingleColor)
            : '—');
    const wastageRaw = roof?.Wastage ?? roof?.WastagePercent;
    const wastageNum =
        wastageRaw != null && wastageRaw !== '' && Number.isFinite(Number(wastageRaw))
            ? Number(wastageRaw)
            : null;

    const batteryDesign = useMemo(() => {
        const display = resolveBatteryDesignDisplay(systemDesign, batteryModelData);
        if (!needsBatteryDesign || batteryFallback.source !== 'default') return display;
        return applyBatteryDesignFallbacks(display, batteryFallback);
    }, [systemDesign, batteryModelData, needsBatteryDesign, batteryFallback]);
    const batteryFallbackHint = needsBatteryDesign ? batteryDesignSourceLabel(batteryFallback.source) : null;

    const ProductNumber = roof?.ProductNumber ?? '';

    const products = useMemo((): V3ProductCardModel[] => {
        const adderPriceSolar = formatAdderPriceDisplay(
            sumAdderPriceForProductType('Solar', dealProductAdders, productTypeAdders),
        );
        const adderPriceRoof = formatAdderPriceDisplay(
            sumAdderPriceForProductType('Roof', dealProductAdders, productTypeAdders),
        );
        const adderPriceBattery = formatAdderPriceDisplay(
            sumAdderPriceForProductType('Battery', dealProductAdders, productTypeAdders),
        );
        const solarRow = oppProductRow(opportunityDetails, 'Solar');
        const roofRow = oppProductRow(opportunityDetails, 'Roof');
        const batteryRow = oppProductRow(opportunityDetails, 'Battery');

        const solarStatus = productStatus(solarRow);
        const roofStatus = productStatus(roofRow);
        const batteryStatus = productStatus(batteryRow);

        const solarFinance = financePartnerInfo(opportunityDetails, 'Solar');
        const roofFinance = financePartnerInfo(opportunityDetails, 'Roof');
        const batteryFinance = financePartnerInfo(opportunityDetails, 'Battery');
        const gutterFinance = financePartnerInfo(opportunityDetails, 'RR');

        const batteryQty = batteryDesign.quantity;

        return [
            {
                kind: 'solar',
                title: 'Solar system',
                subtitle:
                    productionKwh != null ? `${productionKwh.toLocaleString()} kWh/yr` : 'Annual production',
                statusLabel: solarStatus.label,
                statusTone: solarStatus.tone,
                ...solarFinance,
                metrics: [
                    {
                        label: 'System size',
                        value: systemKw != null ? `${systemKw.toFixed(2)} kW` : '—',
                        emphasis: true,
                        liveHint: solarLiveHints.systemKw,
                    },
                    {
                        label: 'Production',
                        value: productionKwh != null ? `${productionKwh.toLocaleString()} kWh` : '—',
                        emphasis: true,
                    },
                    {
                        label: 'Total price',
                        value: solarTotalPrice ?? '—',
                    },
                    { label: 'Adder price', value: adderPriceSolar },
                    ...(normPartner(solarFinance.financePartner) !== 'cash' ? [{ label: 'Solar rate', value: solarRateDisplay }] : []),
                    { label: 'Price per watt', value: epcSolar },
                    {
                        label: 'Multiplier',
                        value: multiplierDisplay,
                        liveHint: solarLiveHints.multiplier,
                    },
                    { label: 'Modules', value: moduleCount != null ? `${moduleCount} panels` : '—' },                  
                    { label: 'Annual usage', value: annualUsage != null ? `${annualUsage.toLocaleString()} kWh` : '—' },
                ],
            },
            {
                kind: 'roof',
                title: 'Roof',
                subtitle: 'Roof replacement',
                statusLabel: roofStatus.label,
                statusTone: roofStatus.tone,
                ...roofFinance,
                showWastage: true,
                wastagePercent: wastageNum ?? undefined,
                shingleLabel: shingle,
                shingleSwatch: ProductNumber,
                fallbackHint: roofFallbackHint,
                metrics: [
                    {
                        label: 'Roof squares',
                        value: roofSq != null ? `${Number(roofSq).toFixed(1)} sq` : '—',
                        emphasis: true,
                    },
                    {
                        label: 'Price / sq ft',
                        value: roofPpsfDisplay,
                        emphasis: true,
                    },
                    {
                        label: 'EPC price',
                        value: roofEpcDisplay,
                        emphasis: true,
                    },
                    { label: 'Adder price', value: adderPriceRoof },
                ],
            },
            {
                kind: 'battery',
                title: 'Battery',
                subtitle: batteryDesign.modelDisplay !== '—' ? batteryDesign.modelDisplay : '—',
                statusLabel: batteryStatus.label,
                statusTone: batteryStatus.tone,
                ...batteryFinance,
                fallbackHint: batteryFallbackHint,
                metrics: [
                    {
                        label: 'Count',
                        value: formatBatteryCount(batteryQty),
                        emphasis: true,
                    },
                    {
                        label: 'Unit price',
                        value: batteryDesign.unitPrice ?? '—',
                    },
                    { label: 'Adder price', value: adderPriceBattery },
                    {
                        label: 'Battery price',
                        value:
                            linkedBatteryQuote?.epcAmount != null
                                ? formatMetricEpc(
                                      linkedBatteryQuote.epcAmount,
                                      linkedBatteryQuote.epcUnit,
                                      linkedBatteryQuote.isLinkedAgreement,
                                  )
                                : '—',
                    },
                    { label: 'Capacity', value: batteryDesign.capacityKwh },
                    { label: 'Model', value: batteryDesign.modelDisplay },
                ],
            },
            {
                kind: 'gutter',
                title: 'Gutter',
                subtitle: 'K-style · 120 linear ft',
                statusLabel: 'Selected',
                statusTone: 'selected',
                financePartner:
                    gutterFinance.financePartner !== 'Select Finance'
                        ? gutterFinance.financePartner
                        : 'GoodLeap',
                financeMethod: gutterFinance.financeMethod || 'Loan',
                metrics: [
                    { label: 'Linear ft', value: '120 ft', emphasis: true },
                    { label: 'Total price', value: '$3,800', emphasis: true },
                    { label: 'Style', value: 'K-style 5"' },
                    { label: 'Guards', value: 'Micro-mesh' },
                    { label: 'Downspouts', value: '4 units' },
                    { label: 'Warranty', value: 'Lifetime' },
                ],
            },
        ];
    }, [
        opportunityDetails,
        systemKw,
        productionKwh,
        solarRateKwh,
        solarRateDisplay,
        epcSolar,
        moduleCount,
        multiplierDisplay,
        solarLiveHints,
        offsetPct,
        roofSq,
        roofPpsfDisplay,
        roofEpcDisplay,
        linkedRoofQuote,
        linkedBatteryQuote,
        linkedSolarQuote,
        solarTotalPrice,
        shingle,
        wastageNum,
        batteryDesign,
        priceQuoteDetails,
        dealProductAdders,
        productTypeAdders,
        annualUsage,
        ProductNumber,
        roofFallbackHint,
        batteryFallbackHint,
    ]);

    const financeSummary = useMemo(() => {
        const lines: V3FinanceLineModel[] = [];
        let total = 0;
        const tags = new Set<string>();

        for (const key of selectedFinanceCells) {
            const parts = key.split('::');
            const partner = parts[0] ?? '';
            const product = parts[1] ?? '';
            const row = financingGrid.find((r) => normPartner(r.partner) === normPartner(partner));
            const cell = row?.prices?.[product];
            if (!cell || !cell.monthly) continue;
            total += cell.monthly;
            tags.add(product);
            lines.push({
                id: key,
                partner,
                method: cell.financeMethod ?? '',
                monthly: cell.monthly,
                productTags: [product],
            });
        }

        const merged = new Map<string, V3FinanceLineModel>();
        for (const line of lines) {
            const k = normPartner(line.partner);
            const prev = merged.get(k);
            if (!prev) {
                merged.set(k, line);
            } else {
                merged.set(k, {
                    ...prev,
                    monthly: prev.monthly + line.monthly,
                    productTags: [...new Set([...prev.productTags, ...line.productTags])],
                });
            }
        }

        return {
            totalMonthly: total,
            lines: Array.from(merged.values()),
            productTags: Array.from(tags),
        };
    }, [selectedFinanceCells, financingGrid]);

    const partnerSyncRows: V3PartnerSyncRow[] = useMemo(() => {
        const names =
            financePartnersList?.map((p: any) => String(p?.Text ?? '').trim()).filter(Boolean) ??
            ['GoodLeap', 'LightReach', 'IGS', 'Sunlight', 'Service Finance'];

        const savedKeys = new Set<string>();
        const oppRows = opportunityDetails?.OpportunityProduct ?? [];
        if (Array.isArray(oppRows)) {
            for (const row of oppRows) {
                const p = String(row?.Finance_Partner_Display_Name ?? row?.Finance_Partner ?? '').trim();
                if (p) savedKeys.add(normPartner(p));
            }
        }

        return names.slice(0, 6).map((name) => {
            const syncKey = name;
            const statusMap = financePartnerSyncStatus as Record<string, { lead?: boolean; contact?: boolean; creditCheck?: boolean; utility?: boolean } | undefined>;
            const status = statusMap[syncKey] ?? statusMap[name];
            const enabled = savedKeys.has(normPartner(name));
            const complete =
                status &&
                (status.lead || status.contact) &&
                status.creditCheck &&
                status.utility;

            let rowStatus: V3PartnerSyncRow['status'] = 'off';
            if (!enabled) rowStatus = 'off';
            else if (complete) rowStatus = 'connected';
            else rowStatus = 'pending';

            return {
                id: normPartner(name),
                name,
                status: rowStatus,
                statusDetail:
                    rowStatus === 'connected'
                        ? 'Synced today'
                        : rowStatus === 'pending'
                          ? 'Pending sync'
                          : 'Off',
                quotesCount: '—',
                signedCount: '—',
                enabled,
            };
        });
    }, [financePartnersList, financePartnerSyncStatus, opportunityDetails]);

    return {
        products,
        financeSummary,
        partnerSyncRows,
        solarMetricsOutOfSync,
        solarMetricsSync,
    };
}
